import React, { Component } from "react";
import { connect } from "react-redux";
import {
  getComprobanteOrdenes,
  putReemplazarComprobanteOrdenApi
} from "../../api/axios";
import { getFacturaOrdenes } from '../../actions/ordenesActions';
import { base64ToArrayBuffer, saveByteArray } from "../../utilities/formatFile";
import OrdenesLoadingSkeleton from "./OrdenesLoadingSkeleton";
import UploadFileOrdenes from "../General/UploadFileOrdenes";
import { toast } from '../../utilities/appToast';
import PropTypes from "prop-types";
import { getLabelEstadoById, idEstadosOrden, normalizeIdEstadoOrden } from "../../helper/ordenHelper";
import { hasAnyValidComprobanteInEstados, isProbablyPdfBase64 } from "../../helper/comprobanteHelper";
import { fileToBase64 } from "../../utilities/formatFile";

class Comprobantes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isUploading: false,
      comprobanteDisponible: false,
      comprobanteChecked: false
    };
  }

  componentDidMount() {
    this.syncDisponible();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.orden?.idOrden;
    const currId = this.props.orden?.idOrden;
    const ordenCambio = prevProps.orden !== this.props.orden;

    if (prevId !== currId || ordenCambio) {
      this.syncDisponible();
    }
  }

  getTrackingUrlFromOrden = (orden) => {
    const domicilio = orden?.entrega?.entregaDomicilio?.urlTracking;
    const sucursal = orden?.entrega?.entregaSucursalCorreo?.urlTracking;
    return [domicilio, sucursal].find((v) => typeof v === "string" && v.trim() !== "") || "";
  };

  getCurrentEstadoId = () => {
    const idEstado = this.props.orden?.estado?.[0]?.idEstado ?? this.props.orden?.estado?.[0]?.IdEstado;
    const parsed = Number(idEstado);
    return Number.isFinite(parsed) ? parsed : null;
  };

  isOrdenAnulada = () => {
    const orden = this.props.orden;
    const isTrueish = (v) => v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
    const getCI = (obj, key) => {
      if (!obj || typeof obj !== "object") return undefined;
      const target = String(key).toLowerCase();
      const foundKey = Object.keys(obj).find((k) => String(k).toLowerCase() === target);
      return foundKey ? obj[foundKey] : undefined;
    };
    const findCIProp = (obj, key) => {
      if (!obj || typeof obj !== "object") return undefined;
      const target = String(key).toLowerCase();
      const foundKey = Object.keys(obj).find((k) => String(k).toLowerCase().includes(target));
      return foundKey ? obj[foundKey] : undefined;
    };

    const anulacion =
      orden?.anulacionMegatone ??
      orden?.AnulacionMegatone ??
      findCIProp(orden, "anulacionmegatone");

    const flagAnulado =
      isTrueish(getCI(anulacion, "anulado")) ||
      isTrueish(getCI(orden, "anulado")) ||
      // si hay motivo de anulación en megatone, también consideramos anulada
      Boolean(getCI(anulacion, "motivoanulacion"));
    if (flagAnulado) return true;

    const estados = Array.isArray(orden?.estado) ? orden.estado : [];
    const getEstadoIdLoose = (estadoItem) => {
      if (!estadoItem || typeof estadoItem !== "object") return null;
      const direct = estadoItem?.idEstado ?? estadoItem?.IdEstado;
      const normalizedDirect = normalizeIdEstadoOrden(direct);
      if (normalizedDirect != null) return normalizedDirect;
      // fallback: buscar una propiedad que se llame parecido a idEstado
      const key = Object.keys(estadoItem).find((k) => String(k).toLowerCase() === "idestado");
      return normalizeIdEstadoOrden(key ? estadoItem[key] : null);
    };

    return estados.some((e) => getEstadoIdLoose(e) === idEstadosOrden.anulado);
  };

  getDisplayEstadoActualId = () => {
    // Mantener consistencia con la grilla: si está anulada (por flag o por historial), el estado actual es ANULADO.
    if (this.isOrdenAnulada()) return idEstadosOrden.anulado;
    return this.getCurrentEstadoId();
  };

  syncDisponible = async () => {
    const orden = this.props.orden;
    if (!orden?.idOrden) return;

    const estadoConComprobante = hasAnyValidComprobanteInEstados(orden);

    // Mismo criterio que usa ListOrdenes: si cualquier estado trae comprobante/archivo, lo consideramos disponible.
    if (estadoConComprobante) {
      this.setState({ comprobanteDisponible: true, comprobanteChecked: true });
      return;
    }

    // Si no viene en la orden, intentamos confirmarlo via API (igual que la lista cuando precarga).
    const idEstadoActual = this.getDisplayEstadoActualId();
    if (!idEstadoActual) {
      this.setState({ comprobanteDisponible: false, comprobanteChecked: true });
      return;
    }

    try {
      const archivo = await getFacturaOrdenes(orden.idOrden, idEstadoActual);
      if (isProbablyPdfBase64(archivo)) {
        this.setState({ comprobanteDisponible: true, comprobanteChecked: true });
      } else {
        throw new Error();
      }
    } catch {
      this.setState({ comprobanteDisponible: false, comprobanteChecked: true })
    }
  };

  descargarOblea = (event) => {
    event.preventDefault();
    const orden = this.props.orden;
    if (!orden?.idOrden) return;

    getComprobanteOrdenes(orden.idOrden)
      .then((res) => {
        const pdfData = base64ToArrayBuffer(res.oblea);
        saveByteArray("Oblea", pdfData);
        toast.success("Descarga realizada");
      })
      .catch((err) => {
        if (err?.isObleaValidationError && Array.isArray(err.messages)) {
          err.messages.forEach((m) => toast.error(m));
          return;
        }
        toast.error("Descarga de oblea fallida");
      });
  };

  replaceComprobante = async ({ idEstado, file }) => {
    const orden = this.props.orden;
    const idOrden = orden?.idOrden;
    const idSeller = orden?.seller?.codigo ?? this.props.idSeller;

    if (!idOrden || !idSeller || !idEstado) {
      toast.error("No se pudo determinar la orden/seller para reemplazar el comprobante");
      return;
    }

    if (!file || typeof file === "string") return;
    const extension = file?.name?.split(".").pop()?.toLowerCase();
    if (extension !== "pdf") {
      toast.warn("La extensión del archivo debe ser .pdf");
      return;
    }

    const confirmed = window.confirm("¿Está seguro de reemplazar el comprobante actual? Esta acción sobrescribirá el archivo existente.");
    if (!confirmed) return;

    this.setState({ isUploading: true });
    try {
      const base64 = await fileToBase64(file);
      await putReemplazarComprobanteOrdenApi({
        idSeller: Number(idSeller),
        fileName: `${idOrden}.pdf`,
        fileContent: base64
      });
      toast.success("Comprobante reemplazado");
      this.syncDisponible();
    } catch (err) {
      const errores = err?.response?.data?.errors || err?.response?.data?.errores;
      if (Array.isArray(errores)) {
        errores.forEach((e) => toast.error(e?.errorMesage ?? e?.ErrorMesage ?? e));
      } else {
        toast.error("No se pudo reemplazar el comprobante");
      }
    } finally {
      this.setState({ isUploading: false });
    }
  };

  render() {
    const orden = this.props.orden;

    const estadoActualId = this.getDisplayEstadoActualId();
    const estadoLabel = getLabelEstadoById(estadoActualId);
    const canEditComprobante = !this.props.userType?.isSoloLectura;

    return (
      <div className="tab-pane p-20 active" name="datos" id="home2" role="tabpanel">
        <div className="row">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between mb-2">
              <div>
                <h5 className="mb-1" style={{ fontWeight: 800 }}>Comprobantes</h5>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Estado actual y disponibilidad del comprobante.
                </div>
              </div>
              {this.state.isUploading && <OrdenesLoadingSkeleton variant="inline" />}
            </div>
          </div>

          <div className="col-12">
            <div className="card border-0" style={{ boxShadow: "0 12px 28px rgba(0,0,0,0.06)", borderRadius: 16 }}>
              <div className="card-body">
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                  <div>
                    <h6 className="mb-1" style={{ fontWeight: 800 }}>
                      Estado actual {estadoLabel?.label ? `- ${estadoLabel.label}` : ""}
                    </h6>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      {this.state.comprobanteChecked ? "Disponibilidad detectada para la orden." : "Verificando disponibilidad..."}
                    </div>
                  </div>
                  <span className={`badge ${this.state.comprobanteDisponible ? "bg-success" : "bg-secondary"}`}>
                    {this.state.comprobanteDisponible ? "Disponible" : "No disponible"}
                  </span>
                </div>

                <div className="mt-3 d-flex flex-wrap gap-2">
                  {this.state.comprobanteDisponible && canEditComprobante && estadoActualId && (
                    <div style={{ width: 300, maxWidth: "100%" }}>
                      <UploadFileOrdenes
                        inputId={`replace-comprobante-${orden?.idOrden}`}
                        acceptType=".pdf"
                        actionLabel="Reemplazar comprobante"
                        dragMessage="Arrastra el nuevo PDF o toca para buscar"
                        dropMessage="Solta el PDF para reemplazar"
                        onFileLoaded={(file) => this.replaceComprobante({ idEstado: estadoActualId, file })}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 mt-3">
            <div className="card border-0" style={{ boxShadow: "0 12px 28px rgba(0,0,0,0.06)", borderRadius: 16 }}>
              <div className="card-body">
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <div>
                    <h6 className="mb-1" style={{ fontWeight: 800 }}>Envios</h6>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Acciones disponibles para el envio de la orden.
                    </div>
                  </div>
                </div>

                <div className="mt-3 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={this.descargarOblea}
                    className="btn btn-sm btn-light border"
                    disabled={this.state.isUploading}
                  >
                    Descargar oblea envio
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Comprobantes.propTypes = {
  orden: PropTypes.shape({
    idOrden: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    estado: PropTypes.array,
    entrega: PropTypes.shape({
      entregaDomicilio: PropTypes.shape({
        urlTracking: PropTypes.string
      }),
      entregaSucursalCorreo: PropTypes.shape({
        urlTracking: PropTypes.string
      })
    }),
    seller: PropTypes.shape({
      codigo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      envioPropio: PropTypes.bool
    })
  }),
  userType: PropTypes.shape({
    isSeller: PropTypes.bool,
    isSellerAdmin: PropTypes.bool,
    isProveedor: PropTypes.bool,
    isCategory: PropTypes.bool,
    isAdminMod: PropTypes.bool,
    isSoloLectura: PropTypes.bool
  }),
  idSeller: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const mapStateToProps = (state) => ({
  orden: state.ordenes.orden,
  userType: state.general.userType,
  idSeller: state.general.idSeller
});

export default connect(mapStateToProps)(Comprobantes);

